import { type ApiErrorCode, errorEnvelopeSchema, REQUEST_ID_HEADER } from '@baby-tracker/contracts';
import { z } from 'zod';

const successEnvelopeShape = z.object({ data: z.unknown() });

/**
 * Thrown for any non-2xx API response or a response that does not match its contract.
 * `code` is the stable API error code; `status` is the HTTP status.
 */
export class ApiClientError extends Error {
    readonly code: ApiErrorCode;
    readonly status: number;
    readonly requestId: string | undefined;
    readonly details: unknown;

    constructor(options: {
        code: ApiErrorCode;
        status: number;
        message: string;
        requestId?: string | undefined;
        details?: unknown;
    }) {
        super(options.message);
        this.name = 'ApiClientError';
        this.code = options.code;
        this.status = options.status;
        this.requestId = options.requestId;
        this.details = options.details;
    }
}

export interface ApiRequestOptions {
    signal?: AbortSignal | undefined;
    body?: unknown;
}

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

async function parseErrorResponse(response: Response): Promise<ApiClientError> {
    const requestId = response.headers.get(REQUEST_ID_HEADER) ?? undefined;
    let body: unknown;
    try {
        body = await response.json();
    } catch {
        body = undefined;
    }
    const parsed = errorEnvelopeSchema.safeParse(body);
    if (parsed.success) {
        return new ApiClientError({
            code: parsed.data.error.code,
            status: response.status,
            message: parsed.data.error.message,
            requestId: parsed.data.error.requestId ?? requestId,
            details: parsed.data.error.details,
        });
    }
    return new ApiClientError({
        code: response.status >= 500 ? 'internal_error' : 'bad_request',
        status: response.status,
        message: `Request failed with status ${response.status}`,
        requestId,
    });
}

async function readSuccess<TSchema extends z.ZodType>(
    response: Response,
    schema: TSchema,
): Promise<z.infer<TSchema>> {
    const body: unknown = await response.json();
    const envelope = successEnvelopeShape.safeParse(body);
    const payload = envelope.success ? schema.safeParse(envelope.data.data) : undefined;
    if (!payload?.success) {
        throw new ApiClientError({
            code: 'internal_error',
            status: response.status,
            message: 'The server returned an unexpected response shape',
            requestId: response.headers.get(REQUEST_ID_HEADER) ?? undefined,
            details: payload?.error.issues ?? envelope.error?.issues,
        });
    }
    return payload.data;
}

async function request<TSchema extends z.ZodType>(
    method: HttpMethod,
    path: string,
    schema: TSchema,
    options: ApiRequestOptions = {},
): Promise<z.infer<TSchema>> {
    let response: Response;
    try {
        response = await fetch(path, {
            method,
            headers: {
                accept: 'application/json',
                ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
            },
            credentials: 'same-origin',
            body: options.body === undefined ? null : JSON.stringify(options.body),
            signal: options.signal ?? null,
        });
    } catch {
        throw new ApiClientError({
            code: 'internal_error',
            status: 0,
            message: 'You are offline. Try again when you are connected.',
        });
    }

    if (!response.ok) {
        throw await parseErrorResponse(response);
    }
    return readSuccess(response, schema);
}

/**
 * Performs a GET against the same-origin API and validates the `{ data }` payload with `schema`.
 */
export function apiGet<TSchema extends z.ZodType>(
    path: string,
    schema: TSchema,
    options: ApiRequestOptions = {},
): Promise<z.infer<TSchema>> {
    return request('GET', path, schema, options);
}

/** Performs a JSON write against the same-origin API and validates the `{ data }` payload. */
export function apiRequest<TSchema extends z.ZodType>(
    method: Exclude<HttpMethod, 'GET'>,
    path: string,
    schema: TSchema,
    options: ApiRequestOptions = {},
): Promise<z.infer<TSchema>> {
    return request(method, path, schema, options);
}

/** Uploads a photo as the raw request body. The path may already include query metadata. */
export async function apiUpload<TSchema extends z.ZodType>(
    path: string,
    file: Blob,
    contentType: string,
    schema: TSchema,
): Promise<z.infer<TSchema>> {
    let response: Response;
    try {
        response = await fetch(path, {
            method: 'POST',
            headers: { accept: 'application/json', 'content-type': contentType },
            credentials: 'same-origin',
            body: file,
        });
    } catch {
        throw new ApiClientError({
            code: 'internal_error',
            status: 0,
            message: 'You are offline. Try again when you are connected.',
        });
    }
    if (!response.ok) {
        throw await parseErrorResponse(response);
    }
    return readSuccess(response, schema);
}
